import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "lme_user_loc";
// NOTE: Stores GPS coordinates only (lat/lng/accuracy/timestamp). Not auth data —
// localStorage is appropriate because the data is non-sensitive and must persist
// across tabs so the user doesn't have to re-share location on every page.

// Returns {loc, loading, error, requestLocation, clearLocation, source}
export const useGeolocation = () => {
  const [state, setState] = useState({ loc: null, loading: false, error: null, source: null });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.lat && cached?.lng) {
          setState((s) => ({ ...s, loc: cached, source: cached.source || "cache" }));
        }
      }
    } catch (err) {
      console.error("[geolocation] cache read failed", err);
    }
  }, []);

  const requestLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        const err = new Error("Geolocation not supported");
        setState((s) => ({ ...s, error: err.message, loading: false }));
        reject(err);
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            source: "gps",
            ts: Date.now(),
          };
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
          } catch (err) {
            console.error("[geolocation] cache write failed", err);
          }
          setState({ loc, loading: false, error: null, source: "gps" });
          resolve(loc);
        },
        (err) => {
          const msg = err.code === 1 ? "Location permission denied" : err.message || "Location unavailable";
          setState((s) => ({ ...s, error: msg, loading: false }));
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 5 * 60 * 1000 }
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error("[geolocation] cache clear failed", err);
    }
    setState({ loc: null, loading: false, error: null, source: null });
  }, []);

  return { ...state, requestLocation, clearLocation };
};
